import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconNames, Icon, CUIText } from '@dotcom/crown-ui';
import paymentImage from '../../../assets/PaymentConfirmation.svg';
import { Layout } from '../../../components/layout';
import { useAnalyticsContext } from '../../../context/analytics-context';
import styles from '../preview.module.scss';

type EmailSplashProps = {
  onPlayAnimations: () => void;
  onContinue: () => void;
  onCloseOverride?: () => void;
  shareUrl?: string;
  onCopyLink?: () => void;
  isCopying?: boolean;
  /** Replaces the legacy Keep shopping event, so only one event is sent per click */
  onTrackKeepShopping?: () => void;
};

export const EmailSplash = ({
  onContinue,
  onPlayAnimations,
  onCloseOverride,
  shareUrl,
  onCopyLink,
  isCopying,
  onTrackKeepShopping,
}: EmailSplashProps) => {
  const { trackViewEmailSplash, trackKeepShopping, isOnEmailSplashScreen } = useAnalyticsContext();
  const { t } = useTranslation();

  const keepShoppingOnClick = () => {
    if (onTrackKeepShopping) {
      onTrackKeepShopping();
    } else {
      trackKeepShopping();
    }
    onContinue();
  };

  const viewDigitalGreetingOnClick = () => {
    trackViewEmailSplash();
    onPlayAnimations();
  };

  useEffect(() => {
    // keep track of splash screen for analytics events
    isOnEmailSplashScreen.current = true;

    return () => {
      isOnEmailSplashScreen.current = false;
    };
  }, [isOnEmailSplashScreen]);

  return (
    <Layout hideToolbar onCloseOverride={onCloseOverride}>
      <div className={`${styles['email-splash']} ${shareUrl ? styles['message-splash'] : ''}`}>
        <img src={paymentImage} alt="Email Sent Confirmation" />
        <CUIText variant="h5" color="textPrimary">
          {t('emailSplash.title')}
        </CUIText>

        <CUIText as="span" color="textPrimary">
          {t('emailSplash.body')}
        </CUIText>

        <div className={styles['buttons']}>
          {shareUrl && <Button onClick={keepShoppingOnClick}>{t('emailSplash.continueButton')}</Button>}
          <Button onClick={viewDigitalGreetingOnClick} variant="secondary">
            <Icon name={IconNames.PlaysmallBold} />
            {t('emailSplash.animationButton')}
          </Button>
          {!shareUrl && <Button onClick={keepShoppingOnClick}>{t('emailSplash.continueButton')}</Button>}
        </div>
        {shareUrl && onCopyLink && (
          <Button
            className={styles['copy-link-again']}
            variant="link"
            type="button"
            onClick={onCopyLink}
            disabled={isCopying}
            testId="copy-link-again-btn"
          >
            {t('ecardSendView.copyLinkAgain')}
            <Icon name={IconNames.Link} />
          </Button>
        )}
      </div>
    </Layout>
  );
};
